define('threadsThreadReplyView',[
  'app',
  'module',
  'backbone',
  'marionette',
  'underscore',
  'settings',
  'dateUtils',
  'bootbox',
  'trumbowyg',
  'mailTemplateRenderer',
  'utils/mail_html_utils',
  'emailAttachmentsView',
  'modalsAlertView',
  'template!threadsThreadReplyView',
  'entities/threads',
  'entities/email_reply_watches',
  // Registers the `htmltemplates:model` handler + the HtmlTemplate model
  // (and its DynamicText relations) used to fetch the MAIL_SIGN signature.
  'entities/caccounts/htmltemplates',
  // Registers `language:collection` — the account's language list, used to
  // ask which signature language to use when no customer is linked.
  'entities/languages'
], function (
  App,
  Module,
  Backbone,
  Marionette,
  _,
  Settings,
  DateUtils,
  bootbox,
  Trumbowyg,
  MailTemplateRenderer,
  MailHtmlUtils,
  EmailAttachmentsView,
  AlertView,
  Tpl
) {
  'use strict';

  // HtmlTemplate type that holds the per-account email signature. A host can
  // ask for another one (options.signatureType, e.g. MAIL_SIGN_SUPPORT for
  // the support inbox).
  var SIGNATURE_TEMPLATE_TYPE = 'MAIL_SIGN';

  Module.exports = Marionette.LayoutView.extend({
    template: Tpl,
    className: 'reply-view',

    ui: {
      header: '.reply-header',
      fromRow: '.reply-from-row',
      from: '.reply-from',
      to: '.reply-to',
      cc: '.reply-cc',
      message: '.reply-message',
      sendBtn: '.btn-send',
      signatureBtn: '.btn-signature',
      notifyOnReply: '.notify-reply-check'
    },

    regions: {
      attachmentsRegion: '.attachments-region'
    },

    events: {
      'click @ui.header': 'onToggleCollapse',
      'click @ui.sendBtn': 'onSend',
      'click .btn-cancel-forward': 'onCancelForward',
      'click @ui.signatureBtn': 'onInsertSignature',
      'change @ui.from': 'onFromChange',
      'input @ui.to': 'onContentChange',
      'input @ui.cc': 'onContentChange'
    },

    initialize: function() {
      // Forward mode (options.forwardMessage = the message JSON being
      // forwarded): recipients start empty, the body is seeded with the quoted
      // original, sending goes to the forward endpoint and the draft
      // auto-save machinery stays off — a forward is composed and sent in one
      // sitting, and an auto-saved draft would reopen as a reply.
      this.forwardMode = !!this.options.forwardMessage;
      this.attachmentsCollection = new Backbone.Collection();
      // Auto-save state: the in-progress reply is persisted as a real Gmail
      // draft so a browser refresh / page switch never loses it. `draftId` is
      // null until the first save creates the draft; then later saves update it.
      this.draftId = null;
      this.dirty = false;
      this.saving = false;
      this.saveTimeout = null;
      // Chosen From header (a send-as alias resolved from the customer's
      // locale). null → the reply goes out from the thread mailbox's own
      // address. The reply ALWAYS sends through the thread mailbox; only this
      // header changes.
      this.fromEmail = null;
      // True once the MAIL_SIGN signature has been injected into the editor
      // body, so we don't insert it twice.
      this.signatureInserted = false;
      this.signatureType = this.options.signatureType || SIGNATURE_TEMPLATE_TYPE;
      // Collapsible mode (opt-in by the host, e.g. the Sales Inbox embed):
      // the composer starts minimised to a single header bar so the email
      // content gets the room, and expands when the bar is clicked. The
      // standalone Threads page doesn't pass the flag, so it stays open.
      this.collapsible = !!this.options.collapsible;
      this.collapsed = this.collapsible && this.options.collapsed !== false;
    },

    onRender: function() {
      this.applyCollapsed();
    },

    onShow: function() {
      this.attachmentsView = new EmailAttachmentsView({
        collection: this.attachmentsCollection
      });
      this.getRegion('attachmentsRegion').show(this.attachmentsView);
      // Template attachments load their bytes asynchronously (url -> base64);
      // keep send disabled until every attachment is ready so we never send a
      // mail missing its proforma / proofsheet PDF.
      // Adding/removing an attachment gates Send until it's loaded and is also
      // an edit that should be auto-saved into the draft.
      this.attachmentsView.on('change', _.bind(function () {
        this.refreshSendState();
        this.onContentChange();
      }, this));
      this.initEditor();
      this.loadFromAddresses();
      if (this.forwardMode) {
        // Seed the editor with the quoted original; the operator writes their
        // note above it, Gmail-style. Recipients are theirs to fill.
        this.setBodyHtml(this.buildForwardQuote(this.options.forwardMessage));
        this.ui.to.focus();
        return;
      }
      // serializeData drops the thread mailbox address from the pre-filled
      // To/Cc, but a mail delivered to one of the account's *aliases* wouldn't
      // match it. Once the full send-address list is available, strip every own
      // address so a reply is never (partly) addressed back to ourselves.
      this.sanitizeRecipients();
    },

    // The "---------- Forwarded message ---------" block Gmail users expect,
    // built from the forwarded message's own data (already loaded with the
    // thread). The original's HTML body is embedded as-is; its attachments are
    // re-attached server-side by the forward endpoint, not here.
    buildForwardQuote: function(m) {
      m = m || {};
      var fromLine = '';
      if (m.from && m.from.length > 0) {
        var f = m.from[0] || {};
        if (f.personal && f.address) { fromLine = f.personal + ' <' + f.address + '>'; }
        else { fromLine = f.address || f.personal || ''; }
      }
      var joinAddresses = function(list) {
        return _.chain(list || [])
          .map(function(a) { return (a && a.address) || ''; })
          .filter(function(a) { return !!a; })
          .value()
          .join(', ');
      };
      var content = m.content || '';
      if (!content && m.contentText) {
        content = _.escape(m.contentText).replace(/\n/g, '<br/>');
      }
      return '<br><br>---------- Forwarded message ---------<br>' +
        'From: ' + _.escape(fromLine) + '<br>' +
        'Date: ' + _.escape(DateUtils.toMDYHHMMFormat(m.sentDate) || '') + '<br>' +
        'Subject: ' + _.escape(m.subject || '') + '<br>' +
        'To: ' + _.escape(joinAddresses(m.to)) + '<br><br>' +
        '<blockquote style="margin:0 0 0 8px;border-left:2px solid #e0e3e8;padding-left:10px;">' +
        content + '</blockquote>';
    },

    onCancelForward: function() {
      this.trigger('forward:cancel');
    },

    // Remove all of the account's own send addresses (mailbox + verified
    // aliases, from threads:getFromMails) from the To/Cc fields, so the "from"
    // side never ends up as a recipient of the reply.
    sanitizeRecipients: function() {
      var self = this;
      App.request('threads:getFromMails').done(function(mails) {
        if (self.isDestroyed) { return; }
        var own = {};
        _.each(mails || [], function(m) {
          var e = ((m && m.email) || '').toLowerCase();
          if (e) { own[e] = true; }
        });
        var mine = ((self.options.mailbox && self.options.mailbox.email) || '').toLowerCase();
        if (mine) { own[mine] = true; }
        if (_.isEmpty(own)) { return; }

        var strip = function($field) {
          if (!$field || !$field.length) { return; }
          var kept = _.chain(($field.val() || '').split(','))
            .map(function(a) { return $.trim(a); })
            .filter(function(a) { return a && !own[a.toLowerCase()]; })
            .value();
          $field.val(kept.join(', '));
        };
        strip(self.ui.to);
        strip(self.ui.cc);
      });
    },

    // Populate the From selector with the user's configured send-from
    // addresses (same list the EmailView popup offers), defaulting to the
    // thread's own mailbox. Hidden until there's a real choice (2+ addresses)
    // so a single-mailbox account stays uncluttered.
    // Locale that drives the From sender, matching the orders board's getFrom:
    // the customer's `locales`, then the account's customerDefaultMailLanguage
    // config; we additionally fall back to the email's detected locale. Absent
    // on the standalone Threads page, where the reply just goes out from the
    // thread mailbox.
    fromLocale: function() {
      return this.options.customerLocale ||
        this.options.templateLocale ||
        Settings.configValue('customerDefaultMailLanguage') ||
        null;
    },

    // Language code of a locale: the part before any region suffix, lowercased
    // (e.g. "fr_BE" / "fr-BE" -> "fr"), so it matches the mails.standard.from keys.
    langOf: function(locale) {
      return locale ? String(locale).toLowerCase().split(/[_-]/)[0] : null;
    },

    // Configured sender address for a locale: mails.standard.from[<lang>],
    // falling back to mails.standard.from.def. '' when unconfigured.
    resolveFromEmail: function(locale) {
      var key = this.langOf(locale);
      // A specific signature type (support) may have its own senders in
      // mails.templates — same rule as the orders' getFrom(template, locale).
      // Without one, a support reply leaves from the support mailbox itself
      // rather than the standard (sales) sender.
      if (this.signatureType !== SIGNATURE_TEMPLATE_TYPE) {
        var own = _.find(Settings.configValue('mails.templates') || [], _.bind(function (t) {
          return t.template === this.signatureType;
        }, this));
        var ownFrom = own && own.from;
        if (!ownFrom) { return ''; }
        return (key && ownFrom[key]) || ownFrom.def || '';
      }
      var cfg = Settings.configValue('mails.standard');
      var fromCfg = cfg && cfg.from;
      if (!fromCfg) { return ''; }
      if (key && fromCfg[key]) { return fromCfg[key]; }
      return fromCfg.def || '';
    },

    // The email of the currently-selected From option (sent in the reply body).
    selectedFromEmail: function() {
      if (!this.ui.from || !this.ui.from.length) { return null; }
      return this.ui.from.find('option:selected').text() || null;
    },

    // Pick the From sender by the customer's locale (Sales Inbox embed). The
    // reply still SENDS through the thread mailbox (so threading never breaks);
    // only the From header changes — Gmail honours it when it's a verified
    // send-as alias of that mailbox. The selector is shown so the agent can
    // see and override the chosen sender.
    loadFromAddresses: function() {
      var self = this;
      if (!this.ui.from || !this.ui.from.length) { return; }
      var locale = this.fromLocale();
      if (!locale) { return; }

      App.request('threads:getFromMails').done(function(mails) {
        if (self.isDestroyed) { return; }
        mails = mails || [];
        if (!mails.length) { return; }

        var $sel = self.ui.from;
        $sel.empty();
        _.each(mails, function(m) {
          $sel.append($('<option></option>').val(m.secId).text(m.email));
        });

        // Match the locale-resolved address to a sendable one; fall back to the
        // thread mailbox's own address when it isn't configured/sendable.
        var fromEmail = self.resolveFromEmail(locale);
        var match = fromEmail ? _.find(mails, function(m) {
          return (m.email || '').toLowerCase() === String(fromEmail).toLowerCase();
        }) : null;
        if (!match) {
          match = _.find(mails, function(m) { return m.secId === self.options.mailSecId; });
        }
        var def = match ? match.secId : (mails[0] && mails[0].secId);
        if (def) { $sel.val(def); }
        // Resolve the From silently — the selector stays hidden; the chosen
        // address is still applied on send as the From (send-as) header.
        self.fromEmail = self.selectedFromEmail();
        if (mails.length > 1) { self.ui.fromRow.prop('hidden', false); }
      });
    },

    onFromChange: function() {
      this.fromEmail = this.selectedFromEmail();
      this.onContentChange();
    },

    // Disable Send while any attachment is still loading or errored.
    refreshSendState: function() {
      if (!this.ui.sendBtn || !this.ui.sendBtn.length) { return; }
      var pending = this.attachmentsCollection.some(function (a) {
        return a.get('error') || !a.get('loaded');
      });
      this.ui.sendBtn.prop('disabled', !!pending).text(pending ? 'Loading attachments…' : 'Send');
    },

    // An edit happened — schedule a debounced auto-save into the Gmail draft.
    // Not while forwarding: the draft endpoints build reply drafts, which
    // would reopen this composer as a reply after a refresh.
    onContentChange: function() {
      if (this.forwardMode) { return; }
      this.dirty = true;
      clearTimeout(this.saveTimeout);
      this.saveTimeout = setTimeout(_.bind(this.saveDraft, this), 3000);
    },

    // Subject to give the draft. The reply has no subject field (it keeps the
    // thread subject), so reuse the target message's subject when we have it.
    replySubject: function() {
      var m = this.options.firstMessage;
      return (m && m.subject) || '';
    },

    // Current composer content, shaped for the draft create/update endpoints —
    // same body normalisation and attachment shape the reply (onSend) uses.
    draftPayload: function() {
      var message = this.getBodyHtml();
      var htmlMessage = MailHtmlUtils.normalize(message);
      var attachments = this.attachmentsCollection.map(function (attachment) {
        return {
          base64: attachment.get('base64'),
          contentDisposition: attachment.get('content-disposition'),
          contentType: attachment.get('content-type'),
          name: attachment.get('name')
        };
      });
      return {
        to: this.ui.to.val() || null,
        cc: this.ui.cc.val() || null,
        bcc: null,
        from: this.fromEmail || null,
        message: htmlMessage,
        attachments: attachments.length ? attachments : null
      };
    },

    // Persist the in-progress reply as a real Gmail draft. The first save
    // creates the draft (POST) and remembers its id; later saves update it
    // (PUT). On a browser refresh / page switch the thread fetch finds this
    // draft and reopens it in the draft composer, so nothing is lost.
    saveDraft: function() {
      clearTimeout(this.saveTimeout);
      if (!this.dirty || this.saving || this.isDestroyed) { return; }

      // Never create an empty draft (untouched or cleared composer).
      var plain = $.trim($('<div>').html(this.getBodyHtml()).text());
      var hasAttachments = this.attachmentsCollection.length > 0;
      if (!plain && !hasAttachments) { return; }

      // Wait for attachments to finish loading so their bytes are in the draft.
      var pending = this.attachmentsCollection.some(function (a) {
        return a.get('error') || !a.get('loaded');
      });
      if (pending) { return; }

      this.saving = true;
      this.dirty = false;
      var self = this;
      var payload = this.draftPayload();
      var subject = this.replySubject();

      var req = this.draftId ?
        App.request('threads:update-draft', this.options.mailSecId, this.options.threadId, this.draftId, payload, subject) :
        App.request('threads:create-draft', this.options.mailSecId, this.options.threadId, payload, subject);

      req.done(function(response) {
        self.saving = false;
        if (response && response.draftId) { self.draftId = response.draftId; }
        // A change landed mid-save — flush it.
        if (self.dirty && !self.isDestroyed) { self.onContentChange(); }
      }).fail(function() {
        self.saving = false;
        self.dirty = true;   // retry on the next change / flush
      });
    },

    // The reply went out through threads:reply, so drop the safety-net Gmail
    // draft we auto-created while composing (otherwise it lingers unsent).
    // Returns a promise so the caller can wait for it before the thread
    // refetches (else the refetch could momentarily reopen the just-sent draft).
    discardAutosavedDraft: function() {
      if (!this.draftId) { return $.Deferred().resolve().promise(); }
      var draftId = this.draftId;
      this.draftId = null;
      return App.request('threads:discard-draft', this.options.mailSecId, this.options.threadId, draftId);
    },

    // Rich-HTML composer (same Trumbowyg editor the EmailView popup uses), so
    // a status-change mail template can be loaded fully-formatted and edited
    // in place rather than opening a separate popup. Trumbowyg keeps the
    // original <textarea> (ui.message) hidden and synced, so html get/set go
    // through `.trumbowyg('html')`.
    initEditor: function() {
      if (this._editorReady || !this.ui.message || !this.ui.message.length) { return; }
      this.ui.message.trumbowyg({
        removeformatPasted: true,
        btns: [
          ['viewHTML'],
          ['undo', 'redo'],
          ['formatting'],
          ['strong', 'em', 'del'],
          ['link'],
          ['insertImage'],
          ['justifyLeft', 'justifyCenter', 'justifyRight'],
          ['unorderedList', 'orderedList'],
          ['removeformat']
        ],
        plugins: { allowTagsFromPaste: true }
      });
      this._editorReady = true;
      // Trumbowyg syncs the editable area back to ui.message and fires
      // `tbwchange` on edits — use it to auto-save the reply as a Gmail draft.
      this.ui.message.on('tbwchange tbwblur', _.bind(this.onContentChange, this));
    },

    onBeforeDestroy: function() {
      // Flush a pending auto-save before the editor is torn down so switching
      // pages mid-compose still persists the draft (fire-and-forget). Read the
      // body while the editor is still alive — before trumbowyg('destroy').
      clearTimeout(this.saveTimeout);
      if (this.dirty) { this.saveDraft(); }
      if (this._editorReady && this.ui.message && this.ui.message.length) {
        try { this.ui.message.trumbowyg('destroy'); } catch (e) { /* already gone */ }
        this._editorReady = false;
      }
    },

    // Current editor body as HTML (empty string before the editor is ready).
    getBodyHtml: function() {
      if (!this._editorReady) { return this.ui.message.val() || ''; }
      return this.ui.message.trumbowyg('html') || '';
    },

    setBodyHtml: function(html) {
      if (this._editorReady) {
        this.ui.message.trumbowyg('html', html || '');
      } else {
        this.ui.message.val(html || '');
      }
    },

    // Reflect the collapsed/collapsible state onto the root element; CSS
    // (reply/view.scss) hides the fields/body/actions while collapsed and
    // turns the header into a clickable bar with a chevron.
    applyCollapsed: function() {
      this.$el.toggleClass('reply-collapsible', this.collapsible);
      this.$el.toggleClass('reply-collapsed', this.collapsible && this.collapsed);
    },

    onToggleCollapse: function() {
      if (!this.collapsible) { return; }
      this.collapsed = !this.collapsed;
      this.applyCollapsed();
      // Focus the editor when the operator opens the composer to write.
      if (!this.collapsed) { this.focusEditor(); }
    },

    // Move focus into the Trumbowyg editable area (falls back to the textarea
    // if the editor isn't initialised yet).
    focusEditor: function() {
      var $editor = this.$('.trumbowyg-editor');
      if ($editor.length) { $editor.focus(); }
      else if (this.ui.message && this.ui.message.length) { this.ui.message.focus(); }
    },

    // Load a rendered mail template into the composer: expand it, render the
    // template body to HTML off-screen, drop it into the editor and seed any
    // template attachments. Called by the Sales Inbox when an offer status
    // change is configured to send a templated mail — instead of opening the
    // EmailView popup, the template lands here for the operator to edit/send.
    // `emailOptions` is the object EmailView.prototype.open would receive.
    loadTemplate: function(emailOptions) {
      var self = this;
      emailOptions = emailOptions || {};

      // Surface the composer so the loaded template is visible.
      if (this.collapsible && this.collapsed) {
        this.collapsed = false;
        this.applyCollapsed();
      }

      MailTemplateRenderer.render(emailOptions).done(function(rendered) {
        if (self.isDestroyed) { return; }
        self.setBodyHtml(rendered && rendered.body ? rendered.body : '');
        self.focusEditor();
        // Persist the loaded template so it survives a refresh like typed text.
        self.onContentChange();
      }).fail(function() {
        if (self.isDestroyed) { return; }
        AlertView.prototype.open('', 'Could not load the mail template for this status.');
      });

      // Attach the same files the popup would (proforma / proofsheet PDFs).
      // The shared EmailAttachmentsView loads each url -> base64, same as in
      // the EmailView popup.
      _.each(emailOptions.attachments || [], function(att) {
        self.attachmentsCollection.add(att);
      });
      // The 'change' event only fires once each attachment finishes loading;
      // reflect the still-loading state right away.
      this.refreshSendState();
    },

    serializeData: function() {
      // Forwarding: the operator picks the recipients, nothing is pre-filled.
      if (this.forwardMode) {
        return { replyTo: '', replyCc: '', headerLabel: 'Forward', isForward: true };
      }

      // firstMessage is the reply target chosen upstream — the latest message
      // we did NOT send (the customer's most recent email).
      var firstMessage = this.options.firstMessage;
      var replyTo = '';
      var replyCc = '';

      if (firstMessage) {
        // The sender of the chosen message — the primary reply recipient.
        var sender = '';
        if (firstMessage.from && firstMessage.from.length > 0) {
          sender = firstMessage.from[0].address || '';
        }
        // Honor an explicit Reply-To header if the original sender set one.
        if (firstMessage.replyTo && firstMessage.replyTo.length > 0) {
          sender = firstMessage.replyTo[0].address || '';
        }
        // To = the sender PLUS everyone the mail was addressed to (its own To
        // list), so a reply to a mail with several To recipients keeps them all.
        replyTo = this.collectTo(firstMessage, sender);
        // Carry forward that email's Cc recipients so the reply keeps everyone
        // in the loop (reply-all style). Drop anyone already on To and our own
        // mailbox, plus blanks/duplicates.
        replyCc = this.collectCc(firstMessage, replyTo);
      }

      return {
        replyTo: replyTo,
        replyCc: replyCc,
        headerLabel: 'Reply',
        isForward: false
      };
    },

    // Build the To string: the sender first, then the replied-to email's own
    // To recipients — minus our own mailbox and duplicates (case-insensitive).
    collectTo: function(message, sender) {
      var ownAddress = ((this.options.mailbox && this.options.mailbox.email) || '').toLowerCase();
      var seen = {};
      var out = [];
      var add = function(email) {
        email = email || '';
        var key = email.toLowerCase();
        if (!email || key === ownAddress || seen[key]) { return; }
        seen[key] = true;
        out.push(email);
      };

      add(sender);
      _.each((message && message.to) || [], function(addr) {
        add((addr && addr.address) || '');
      });

      return out.join(', ');
    },

    // Build the Cc string from the replied-to email's Cc list (comma-joined),
    // excluding any address already on To (which can now hold several), our own
    // mailbox, and duplicates.
    collectCc: function(message, replyTo) {
      var cc = (message && message.cc) || [];
      if (!cc.length) { return ''; }

      var ownAddress = ((this.options.mailbox && this.options.mailbox.email) || '').toLowerCase();
      var toSet = {};
      _.each((replyTo || '').split(','), function(a) {
        var key = (a || '').trim().toLowerCase();
        if (key) { toSet[key] = true; }
      });
      var seen = {};
      var out = [];

      _.each(cc, function(addr) {
        var email = (addr && addr.address) || '';
        var key = email.toLowerCase();
        if (!email) { return; }
        if (toSet[key] || key === ownAddress) { return; }
        if (seen[key]) { return; }
        seen[key] = true;
        out.push(email);
      });

      return out.join(', ');
    },

    // --- Signature (MAIL_SIGN html template) -----------------------------
    // Fetch the account's MAIL_SIGN template and inject it directly into the
    // rich-HTML editor body (so the operator sees and can edit it inline, and
    // it's part of the message itself rather than a separate appended panel).
    //
    // Locale handling: when a customer is linked we trust the email's locale
    // and pick automatically. When no customer is linked we can't infer the
    // language, so we ask the operator which signature language to use.
    onInsertSignature: function () {
      if (this.signatureInserted) { return; }
      var self = this;
      this.ui.signatureBtn.prop('disabled', true).text('Loading…');

      var model = App.request('htmltemplates:model', { templateType: self.signatureType });
      model.fetchByType()
        .done(function () {
          self.ui.signatureBtn.prop('disabled', false).text('Insert signature');
          if (self.options.customerLinked) {
            self.applySignature(model, self.autoLocale(model));
          } else {
            self.askLanguageThenApply(model);
          }
        })
        .fail(function (xhr) {
          self.ui.signatureBtn.prop('disabled', false).text('Insert signature');
          // 404 → no template of that type exists for this account.
          var msg = (xhr && xhr.status === 404) ?
            'No signature is configured for your account (template type ' + self.signatureType + ').' :
            'Could not load the signature.';
          AlertView.prototype.open('', msg);
        });
    },

    // First locale that actually has signature text — the email's locale (when
    // the composer was given one), then the operator's UI language, then
    // English. Falls back to the first candidate so build() can report "no
    // content" downstream.
    autoLocale: function (model) {
      var content = model.get('content');
      var candidates = _.uniq(_.compact([
        this.options.templateLocale,
        Settings.get('lang'),
        'en'
      ]));
      var withContent = content && _.find(candidates, function (c) {
        return !!content.getTextInLocale(c);
      });
      return withContent || candidates[0] || 'en';
    },

    // No customer linked → ask which language to use. Offer only the languages
    // the signature is actually translated into (so the pick always renders).
    // If there's a single such language we skip the prompt; if there are none
    // the template has no content at all.
    askLanguageThenApply: function (model) {
      var self = this;
      var content = model.get('content');

      App.request('language:collection', []).getLanguages().done(function (languages) {
        var available = languages.filter(function (l) {
          return content && !!content.getTextInLocale(l.get('abbreviation'));
        });
        var pool = available.length ? available : languages.models;

        var options = _.map(pool, function (l) {
          return { value: l.get('abbreviation'), text: l.get('name') || l.get('abbreviation') };
        });

        if (!options.length) {
          AlertView.prototype.open('', 'No signature is configured for your account.');
          return;
        }
        if (options.length === 1) {
          self.applySignature(model, options[0].value);
          return;
        }

        bootbox.prompt({
          title: 'Signature language',
          inputType: 'select',
          inputOptions: options,
          value: self.autoLocale(model),
          callback: function (locale) {
            // null when the operator cancels the dialog.
            if (locale) { self.applySignature(model, locale); }
          }
        });
      });
    },

    // Render the signature for a specific locale and inject it, or alert if that
    // locale has no content / the template references unavailable variables.
    // Resolves the linked customer first so the template can use customer.*
    // variables (e.g. a greeting / name).
    applySignature: function (model, locale) {
      var self = this;
      this.resolveCustomer(function (customer) {
        self.buildAndInsertSignature(model, locale, customer);
      });
    },

    // Resolve the linked customer (by endUserId) so the signature template can
    // reference customer fields. Cached after the first fetch; resolves to {}
    // when no customer is linked (e.g. a reply opened outside the sales inbox)
    // or the fetch fails, so the signature still renders.
    resolveCustomer: function (cb) {
      if (this.customerData) { cb(this.customerData); return; }
      var endUserId = this.options.customerEndUserId;
      if (!endUserId) { cb({}); return; }

      var self = this;
      var customer = App.request('customers:entity:end-user-id', endUserId);
      customer.fetch()
        .done(function () { self.customerData = customer.toJSON(); cb(self.customerData); })
        .fail(function () { cb({}); });
    },

    buildAndInsertSignature: function (model, locale, customer) {
      var content = model.get('content');
      if (!content || !content.getTextInLocale(locale)) {
        AlertView.prototype.open('', 'No signature is configured for your account.');
        return;
      }

      var data = {
        user: Settings.get('currentUser') || {},
        customer: customer || {},
        locale: locale
      };

      var html;
      try {
        html = model.build(data, null, locale);
      } catch (e) {
        // The template referenced a variable we don't supply here.
        AlertView.prototype.open('', 'Could not render the signature: ' + e.message);
        return;
      }
      if (!html) {
        AlertView.prototype.open('', 'No signature is configured for your account.');
        return;
      }

      // Inject the signature at the end of the editor body, separated from any
      // text already written. It becomes part of the message and stays fully
      // editable; the operator removes it by editing the body.
      var current = this.getBodyHtml();
      var hasText = !!$.trim($('<div>').html(current).text());
      this.setBodyHtml(current + (hasText ? MailHtmlUtils.blankLineAfter(current) : '') + html);

      this.signatureInserted = true;
      this.ui.signatureBtn.prop('disabled', true).text('Signature added');
      this.focusEditor();
      // Persist the added signature so it survives a refresh like typed text.
      this.onContentChange();
    },

    // Reset the signature affordance after a send (the editor was cleared).
    resetSignatureState: function () {
      this.signatureInserted = false;
      if (this.ui.signatureBtn && this.ui.signatureBtn.length) {
        this.ui.signatureBtn.prop('disabled', false).text('Insert signature');
      }
    },

    onSend: function() {
      var to = this.ui.to.val();
      var cc = this.ui.cc.val();
      // The composer is a rich-HTML editor, so the body is already HTML. Match
      // EmailView's normalisation (drop <p> wrappers, keep <br>) so spacing is
      // consistent with the popup composer.
      var message = this.getBodyHtml();
      var plain = $.trim($('<div>').html(message).text());

      if (!to || !plain) return;

      // Sending supersedes any pending auto-save.
      clearTimeout(this.saveTimeout);

      // The signature, when used, is already part of the editor body, so the
      // message HTML is complete as-is — no separate append.
      var htmlMessage = MailHtmlUtils.normalize(message);

      var attachments = [];
      _.each(this.attachmentsCollection.models, function (attachment) {
        attachments.push({
          base64: attachment.get('base64'),
          contentDisposition: attachment.get('content-disposition'),
          contentType: attachment.get('content-type'),
          name: attachment.get('name')
        });
      });

      this.ui.sendBtn.prop('disabled', true).text('Sending...');

      var payload = {
        to: to,
        cc: cc || null,
        bcc: null,
        from: this.fromEmail || null,
        message: htmlMessage,
        attachments: attachments.length > 0 ? attachments : null,
        // "Notify me of a reply" (EmailReplyWatchService). The inbox item the
        // composer is embedded in, if any, is kept on the watch.
        notifyOnReply: this.ui.notifyOnReply.is(':checked'),
        salesInboxItemSecId: this.options.salesInboxItemSecId || null
      };

      // Forward goes to the message-level forward endpoint (the server also
      // re-attaches the original's own attachments); the reply keeps its
      // thread endpoint. Both always send through the thread's own mailbox;
      // `from` only overrides the visible sender header, resolved from the
      // customer's locale and honoured by Gmail as a send-as alias.
      var request;
      if (this.forwardMode) {
        request = App.request('threads:forward', this.options.mailSecId, this.options.threadId,
          this.options.forwardMessage.id, payload);
      } else {
        request = App.request('threads:reply', this.options.mailSecId, this.options.threadId, payload);
      }

      request.done(_.bind(function(response) {
        App.request('emailReplyWatches:warn', payload.notifyOnReply, response);
        this.ui.notifyOnReply.prop('checked', false);
        this.dirty = false;
        this.setBodyHtml('');
        this.attachmentsCollection.reset();
        this.resetSignatureState();
        this.ui.sendBtn.prop('disabled', false).text('Send');
        // Reply is out — remove the safety-net draft we auto-saved while typing,
        // THEN let the thread refresh so the refetch never reopens it.
        this.discardAutosavedDraft().always(_.bind(function() {
          this.trigger('reply:sent');
        }, this));
      }, this)).fail(_.bind(function() {
        this.ui.sendBtn.prop('disabled', false).text('Send');
      }, this));
    }
  });
});

